import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { ISyncSpeaekrsToKnovioDTO } from '../../../../../../../api/connections.dao';
import { MeetingApiParams } from '../connection-form.viewmodel';
import { ConnectionsDAO } from '../../../../../../../api/implementations/connections.dao';
import { alertServerError, showNotification } from '../../../../../../global/modal/alerts-host/alert-templates';
import { IAttendeeOrganizationDTO } from '../../../../../../../api/attendees.dao';
import { IImageWithUrl } from '../../../../../../../api/shared.dto';
import {
    MAX_SPEAKER_BIOGRAPHY_LENGTH,
    SYNC_SPEAKERS_ERROR_MESSAGE,
    SYNC_SPEAKERS_MAX_BIO_LENGTH_ERROR,
    SYNC_SPEAKERS_SUCCESS_MESSAGE
} from './sync-speakers-constants';
import { IGlobalStorage } from '../../../../../../../infrastructure/global-storage';
import { TRACKING_EVENTS } from '../../../../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../../../../tracking-service/tracking-service-provider';

interface ISyncSpeakersListItem {
    id: number;
    firstName: string;
    lastName: string;
    title: string;
    organizationName: string;
    biography: string;
    bioExpanded: boolean;
    profileImageWithUrl?: IImageWithUrl;
}

interface IOrganizationsForSpeakersSync {
    presenters: IAttendeeOrganizationDTO[];
    hosts: IAttendeeOrganizationDTO[];
    supports?: IAttendeeOrganizationDTO[];
}

class SyncSpeakersVM {
    private embedCodeId: string;
    private meetingConnectionId: number;
    private initSpeakerList: ISyncSpeakersListItem[] = [];
    @mobx.observable
    private isOpenSyncSpeakersModal: boolean;
    @mobx.observable
    private enabledSpeakers: ISyncSpeakersListItem[] = [];
    @mobx.observable
    private disabledSpeakers: ISyncSpeakersListItem[] = [];
    @mobx.observable
    private isLoading: boolean;

    constructor(
        private organizations: IOrganizationsForSpeakersSync,
        private readonly meetingApiParams: MeetingApiParams,
        private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly connectionsDAO: ConnectionsDAO = IoC.Container.get(ConnectionsDAO),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
    ) {
        this.init();
    }

    public getIsOpenSyncSpeakerModal = (): boolean => {
        return this.isOpenSyncSpeakersModal;
    };

    public getIsLoading = (): boolean => {
        return this.isLoading;
    };

    public getEnabledSpeakers = (): ISyncSpeakersListItem[] => {
        return this.enabledSpeakers;
    };

    public getDisabledSpeakers = (): ISyncSpeakersListItem[] => {
        return this.disabledSpeakers;
    };

    @mobx.action
    public setEnabledSpeakers = (speakers: ISyncSpeakersListItem[]): void => {
        this.enabledSpeakers = [...speakers];
    };

    @mobx.action
    public setDisabledSpeakers = (speakers: ISyncSpeakersListItem[]): void => {
        this.disabledSpeakers = [...speakers];
    };

    public setEmbedCodeId = (embedCodeId: string): void => {
        this.embedCodeId = embedCodeId;
    };

    public setMeetingConnectionId = (meetingConnectionId: number): void => {
        this.meetingConnectionId = meetingConnectionId;
    };

    @mobx.action
    public toggleBio = (speaker: ISyncSpeakersListItem): void => {
        speaker.bioExpanded = !speaker.bioExpanded;
    };

    @mobx.action
    public enableSpeaker = (speaker: ISyncSpeakersListItem): void => {
        this.disabledSpeakers = this.disabledSpeakers.filter(item => item.id !== speaker.id);
        this.enabledSpeakers.push(speaker);
    };

    @mobx.action
    public disableSpeaker = (speaker: ISyncSpeakersListItem): void => {
        this.enabledSpeakers = this.enabledSpeakers.filter(item => item.id !== speaker.id);
        this.disabledSpeakers.push(speaker);
        speaker.bioExpanded = false;
    };

    @mobx.action
    public openSyncSpeakersModal = (): void => {
        this.isOpenSyncSpeakersModal = true;
    };

    @mobx.action
    public closeSyncSpeakersModal = (): void => {
        this.isOpenSyncSpeakersModal = false;
        this.cleanup();
    };

    public trackEventMixPanel = (syncSpeakersName: string, syncSpeakersData: MeetingApiParams & ISyncSpeaekrsToKnovioDTO): void => {
        const trackSyncSpeakersDTO = {
            'Account ID': syncSpeakersData.accountId,
            'Account Name': this.globalStorage.getAccount()?.name,
            'Event ID': syncSpeakersData.eventId,
            'Event Name': this.globalStorage.getEvent()?.name,
            'Meeting ID': this.meetingApiParams.meetingId,
            'Connection ID': this.meetingConnectionId,
            'Number of Speakers': syncSpeakersData.speakers_info.length
        };

        this.trackingService.get().track(syncSpeakersName, trackSyncSpeakersDTO);
    };

    public applySpeakersSync = async (): Promise<void> => {
        const isValidSpeakersBio = this.validateSpeakersBio(this.enabledSpeakers);
        if (!isValidSpeakersBio) {
            alertServerError('Error', SYNC_SPEAKERS_MAX_BIO_LENGTH_ERROR);
            return;
        }
        try {
            mobx.runInAction(() => this.isLoading = true);
            const syncSpeaekerDTO: MeetingApiParams & ISyncSpeaekrsToKnovioDTO = {
                eventId: this.meetingApiParams.eventId,
                accountId: this.meetingApiParams.accountId,
                embed_id: this.embedCodeId,
                speakers_info: this.enabledSpeakers.map((item, index) => ({
                    order: index,
                    name: `${item.firstName} ${item.lastName}`,
                    details: {
                        title: item.title || '',
                        organization: item.organizationName || '',
                        description: item.biography || '',
                        photo_url: item.profileImageWithUrl?.assetUrl || null,
                    }
                }))
            };
            await this.connectionsDAO.syncSpeakersToKnovio(syncSpeaekerDTO);
            this.trackEventMixPanel(TRACKING_EVENTS.SYNC_SPEAKERS, syncSpeaekerDTO);
            showNotification(SYNC_SPEAKERS_SUCCESS_MESSAGE);
            this.closeSyncSpeakersModal();
        } catch {
            alertServerError('Error', SYNC_SPEAKERS_ERROR_MESSAGE);
        } finally {
            mobx.runInAction(() => this.isLoading = false);
        }
    };

    private init = (): void => {
        this.initSpeakerList = this.createSortedSpeakerList();
        this.enabledSpeakers = this.initSpeakerList;
    };

    private validateSpeakersBio = (speakers: ISyncSpeakersListItem[]): boolean => {
        return !speakers.some(item => {
            return (item.biography?.length ?? 0) > MAX_SPEAKER_BIOGRAPHY_LENGTH;
        });
    };

    private createSpeakerListFromOrganizations = (organizations: IAttendeeOrganizationDTO[]): ISyncSpeakersListItem[] => {
        return organizations.reduce((prevValue, currentValue) => {
            const attendees = currentValue.attendees;
            return prevValue = [
                ...prevValue,
                ...attendees.map<ISyncSpeakersListItem>(attendee => {
                    return {
                        id: attendee.attendeeId,
                        firstName: attendee.firstName,
                        lastName: attendee.lastName,
                        title: attendee.title,
                        organizationName: currentValue.name,
                        biography: attendee.biography,
                        profileImageWithUrl: attendee.profileImageWithUrl,
                        bioExpanded: false,
                    };
                })
            ];
        }, []);
    };

    private sortSpeakerList = (speakerList: ISyncSpeakersListItem[]): void => {
        speakerList.sort(function(a, b) {
            return a.organizationName.localeCompare(b.organizationName) || a.lastName.localeCompare(b.lastName);
        });
    };

    private createSortedSpeakerList = (): ISyncSpeakersListItem[] => {
        const presenters = this.createSpeakerListFromOrganizations(this.organizations?.presenters || []);
        const hosts = this.createSpeakerListFromOrganizations(this.organizations?.hosts || []);
        this.sortSpeakerList(presenters);
        this.sortSpeakerList(hosts);

        return [...presenters, ...hosts];
    };

    @mobx.action
    private cleanup = (): void => {
        this.disabledSpeakers = [];
        this.enabledSpeakers = this.initSpeakerList;
    };
}

export { SyncSpeakersVM, ISyncSpeakersListItem, IOrganizationsForSpeakersSync };
